import React from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { selectedBranchAtom, themeAtom, userDataAtom } from '../../Atoms';
import useTranslate from '../../Hooks/useTranslate';
import StyledSwitch from '../Inputs/StyledSwitch';
import {
  askNotificationPermission,
  isNotificationGranted,
  isNotificationSupported,
} from '../../Helpers/notifications';
import { CenteredFlexBox, ColumnFlexBox } from '../FlexBox';
import BranchSheet from './BranchSheet';

const getInitials = (userData) =>
  `${userData?.name?.[0] ?? ''}${userData?.surname?.[0] ?? ''}`.toUpperCase();

/**
 * MobileMenu — the "Menu" tab (see MOBILE_PLAN.md §F(11), mockup screen 11):
 * a gradient header with the user's initials/name/role, a single branch-switch
 * card, quick actions (change password, dark mode, notifications, release
 * notes) and a card-style logout button.
 */
const MobileMenu = function MobileMenu({
  onLogout = () => {},
  version = null,
  userRoles = null,
}) {
  const theme = useTheme();
  const translate = useTranslate();
  const navigate = useNavigate();
  const userData = useAtomValue(userDataAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const [themeMode, setThemeMode] = useAtom(themeAtom);
  const [branchSheetOpen, setBranchSheetOpen] = React.useState(false);

  const branchName = selectedBranch?.branchDescription ?? '';
  const fullName = `${userData?.name ?? ''} ${userData?.surname ?? ''}`.trim();
  const roleId = userRoles?.[0]?.roleId;
  const roleLabel = roleId ? translate(roleId) : '';
  const subtitle =
    [roleLabel, branchName].filter(Boolean).join(' · ') ||
    userData?.email ||
    '';
  const isDark = themeMode === 'dark';
  const versionLabel = version
    ? `${version.major}.${version.minor}.${version.revision}`
    : null;

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setThemeMode(next);
    localStorage.setItem('theme', next);
  };

  return (
    <>
      <Box
        sx={{
          px: 2.5,
          pt: 1.5,
          pb: 2.75,
          color: '#fff',
          background: 'linear-gradient(120deg, #1D5A9D 20%, #61B340 130%)',
        }}
      >
        <Typography
          sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-.01em' }}
        >
          {translate('MENU')}
        </Typography>
        <CenteredFlexBox sx={{ gap: 1.75, mt: 2 }}>
          <Avatar
            sx={{
              width: 54,
              height: 54,
              fontSize: 18,
              fontWeight: 700,
              color: '#fff',
              bgcolor: 'rgba(255,255,255,.22)',
            }}
          >
            {getInitials(userData)}
          </Avatar>
          <ColumnFlexBox sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600 }} noWrap>
              {fullName}
            </Typography>
            {subtitle ? (
              <Typography sx={{ fontSize: 13, opacity: 0.85 }} noWrap>
                {subtitle}
              </Typography>
            ) : null}
          </ColumnFlexBox>
        </CenteredFlexBox>
      </Box>

      <ColumnFlexBox sx={{ p: 2, gap: 1.5 }}>
        <Paper
          onClick={() => setBranchSheetOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            p: '14px 16px',
            borderRadius: '16px',
            cursor: 'pointer',
          }}
        >
          <ApartmentRoundedIcon sx={{ color: 'primary.main' }} />
          <ColumnFlexBox sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 12, color: theme.custom.mobile.mutedColor }}
            >
              {translate('BRANCH')}
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }} noWrap>
              {branchName}
            </Typography>
          </ColumnFlexBox>
          <ChevronRightRoundedIcon sx={{ color: '#cbd5e1' }} />
        </Paper>

        <Paper sx={{ overflow: 'hidden', borderRadius: '16px' }}>
          <List disablePadding>
            <ListItemButton divider onClick={() => navigate('/changePassword')}>
              <ListItemIcon sx={{ color: theme.custom.mobile.mutedColor }}>
                <LockResetRoundedIcon />
              </ListItemIcon>
              <ListItemText primary={translate('CHANGE_PASSWORD')} />
              <ChevronRightRoundedIcon
                sx={{ color: theme.custom.mobile.mutedColor }}
              />
            </ListItemButton>

            <ListItemButton divider onClick={toggleTheme}>
              <ListItemIcon sx={{ color: theme.custom.mobile.mutedColor }}>
                <DarkModeRoundedIcon />
              </ListItemIcon>
              <ListItemText primary={translate('DARK_MODE')} />
              <StyledSwitch
                id="mobile-dark-mode"
                size="small"
                checked={isDark}
                onChange={toggleTheme}
                onClick={(event) => event.stopPropagation()}
                sx={{ m: 0 }}
              />
            </ListItemButton>

            {isNotificationSupported() ? (
              <ListItemButton divider onClick={askNotificationPermission}>
                <ListItemIcon sx={{ color: theme.custom.mobile.mutedColor }}>
                  <NotificationsNoneRoundedIcon />
                </ListItemIcon>
                <ListItemText primary={translate('NOTIFICATIONS')} />
                {isNotificationGranted() ? (
                  <Typography
                    variant="body2"
                    sx={{ color: 'success.main', fontWeight: 600 }}
                  >
                    {translate('ACTIVE')}
                  </Typography>
                ) : (
                  <ChevronRightRoundedIcon
                    sx={{ color: theme.custom.mobile.mutedColor }}
                  />
                )}
              </ListItemButton>
            ) : null}

            <ListItemButton>
              <ListItemIcon sx={{ color: theme.custom.mobile.mutedColor }}>
                <InfoOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary={translate('RELEASE_NOTES')} />
              {versionLabel ? (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: '11px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#8a6300',
                    bgcolor: '#fff2d6',
                  }}
                >
                  {`v${versionLabel}`}
                </Box>
              ) : null}
            </ListItemButton>
          </List>
        </Paper>

        <ButtonBase
          onClick={onLogout}
          sx={{
            mt: 0.5,
            width: '100%',
            height: 52,
            gap: 1,
            borderRadius: '16px',
            bgcolor: 'background.paper',
            border: '1px solid #fadadd',
            color: '#d32f2f',
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          <LogoutRoundedIcon sx={{ fontSize: 20 }} />
          {translate('LOGOUT')}
        </ButtonBase>
      </ColumnFlexBox>

      <BranchSheet
        open={branchSheetOpen}
        onClose={() => setBranchSheetOpen(false)}
      />
    </>
  );
};

export default MobileMenu;
